import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../src/loginPage.css';
import { IconButton, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { adminLogin } from '../api/authApi';
import { clearSession, isAuthenticated } from '../utils/auth';
import { getApiErrorMessage } from '../utils/apiError';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState({ email: '', password: '' });
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        if (isAuthenticated()) {
            navigate('/Dashboard', { replace: true });
        } else {
            // Expired or tampered token: clear it so it can't linger in storage.
            clearSession();
        }
    }, [navigate])

    const handleSubmit = async (event) => {
        event.preventDefault();

        // guards against a second submit slipping in before the re-render
        if (submitting) {
            return;
        }

        setError({ email: '', password: '' });

        let formIsValid = true;
        let errors = { email: '', password: '' };

        if (!email) {
            formIsValid = false;
            errors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            formIsValid = false;
            errors.email = 'Please enter a valid email address.';
        }

        if (!password) {
            formIsValid = false;
            errors.password = 'Password is required.';
        } else if (password.length < 6) {
            formIsValid = false;
            errors.password = 'Password must be at least 6 characters long.';
        }

        if (!formIsValid) {
            setError(errors);
            return;
        }

        setSubmitting(true);
        try {

            const response = await adminLogin({ email, password })
            if (response.data.status === true) {
                localStorage.setItem('role', response?.data?.data.detaisls);
                localStorage.setItem('accessToken', response?.data?.data.token);
                if (response?.data?.data?.refreshToken) {
                    localStorage.setItem('refreshToken', response.data.data.refreshToken);
                }
                localStorage.setItem('emailId', email);
                localStorage.setItem("loginType", response?.data?.data.detaisls);
                navigate('/Dashboard');
            } else {
                setError({ email: '', password: response?.data?.message || response?.data?.msg || 'Invalid email or password.' });
            }
        } catch (err) {
            console.error('Error during login:', err);
            setError({
                email: '',
                password: getApiErrorMessage(err, 'Login failed, please try again.')
            });
        } finally {
            // re-enabled only once the response (or failure) is in
            setSubmitting(false);
        }
    };

    return (
        <div className="login-container">
            <div>
                <h2>Log in</h2>

                <form onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            className={error.email ? 'input-error' : ''}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setError((prev) => ({ ...prev, email: '' }));
                            }}
                            placeholder="Enter your email"
                            maxLength={50}
                            minLength={5}
                        />
                        {error.email && <p className="error-message">{error.email}</p>}
                    </div>

                    <div className="input-group">
                        <label htmlFor="password">Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                className={error.password ? 'input-error' : ''}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setError((prev) => ({ ...prev, password: '' }));
                                }}
                                placeholder="Enter your password"
                            />
                            <IconButton
                                className="password-toggle"
                                onClick={() => setShowPassword((prev) => !prev)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                edge="end"
                                size="small"
                                disableRipple
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: 10,
                                    transform: 'translateY(-50%)',
                                }}
                            >
                                {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                            </IconButton>
                        </div>
                        {error.password && <p className="error-message">{error.password}</p>}
                    </div>
                    <div className="forgot-password-row">
                        <button
                            type="button"
                            className="link-button"
                            onClick={() => navigate("/forgot-password", { state: { email } })}
                        >
                            Forgot Password?
                        </button>
                    </div>
                    <button className='login-button' type="submit" disabled={submitting}>
                        {submitting && <CircularProgress size={16} color="inherit" />}
                        {submitting ? 'Logging in...' : 'Log in to ChessWyze'}
                    </button>

                </form>
            </div>
        </div>
    );
};

export default Login;
